/**
 * Helpers de data/hora locale-aware. Substituem as cópias de `tempoRelativo()`
 * espalhadas em comunicados.tsx, notificacoes.tsx e NotificationBell.tsx.
 *
 * Todos usam Intl.DateTimeFormat com tag BCP-47 derivada do LocaleCode
 * (pt → pt-BR, en → en-US, es → es-ES). Pluralização via i18next.
 */
import type { TFunction } from "i18next";
import type { LocaleCode } from "@/lib/preferences";

// pt → pt-BR, en → en-US, es → es-ES
function intlLocale(loc: LocaleCode): string {
  if (loc === "pt") return "pt-BR";
  if (loc === "en") return "en-US";
  return "es-ES";
}

/**
 * "agora" / "há 3 min" / "há 2 h" / "há 5 d" / "08 mai 2026"
 *
 * Usa chaves do namespace `time`:
 *   time.now, time.minutesAgo_one/_other, time.hoursAgo_one/_other,
 *   time.daysAgo_one/_other
 *
 * Após 7 dias, mostra data formatada via Intl.
 */
export function formatRelativeTime(iso: string, locale: LocaleCode, t: TFunction): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);

  if (s < 60)        return t("time.now");
  if (s < 3600)      return t("time.minutesAgo", { count: Math.floor(s / 60) });
  if (s < 86400)     return t("time.hoursAgo",   { count: Math.floor(s / 3600) });
  if (s < 86400 * 7) return t("time.daysAgo",    { count: Math.floor(s / 86400) });

  // Fallback: data abreviada localizada
  return new Date(iso).toLocaleDateString(intlLocale(locale), {
    day:   "2-digit",
    month: "short",
    year:  "numeric",
  });
}

/**
 * "08 mai 2026, 14:30" — pra notificações antigas que mostram timestamp completo.
 */
export function formatFullDateTime(iso: string, locale: LocaleCode): string {
  return new Date(iso).toLocaleString(intlLocale(locale), {
    day:    "2-digit",
    month:  "short",
    year:   "numeric",
    hour:   "2-digit",
    minute: "2-digit",
  });
}
